<template>
  <div>
    <template v-if="noConnectedSites">
      <GuideToSetUpSite
        :is-more-than-editor="showMoreThanEditorAuthority"
      />
    </template>
    <template v-else>
      <div class="p-box">
        <form class="p-box__header">
          <div class="p-filtersA">
            <div class="p-filtersA__left">
              <div class="c-buttonFilter" @click="openModal">
                {{ $t('pages.CompaniesReviews.filters') }}
                <div class="c-buttonFilter__icon">
                  <Icon name="iconFilter" />
                </div>
              </div>
            </div>
            <div class="p-filtersA__right">
              <form class="c-searchField" onsubmit="return false;">
                <input
                  v-model="searchQuery"
                  :disabled="loading"
                  type="text"
                  class="searchForm"
                  :placeholder="$t('labels.search')"
                  @keydown.enter="search"
                >
                <Icon name="iconSearch" />
              </form>
              <!--
                口コミ一覧画面
                  - 投稿日のソート:
                    - posted_atではなくreviewer_edited_atで行う。
                    - 表記は「投稿日」のままで表記する。
                    - 理由: Google Mapsでは変更されたコメントは新規投稿として扱われるため、
                            Google Mapsと同様にreviewer_edited_atでソートするほうが変更されたコメントが上に来る。

                  - 対象口コミの取得:
                    - posted_atではなくreviewer_edited_atの日付レンジで取得する。
              -->
              <div class="sortForm c-buttonSort">
                <button
                  class="c-buttonSort__item"
                  :class="`is-${sortKey === 'reviewer_edited_at' && sortOrder}`"
                  type="button"
                  :disabled="loading"
                  @click="sort('reviewer_edited_at')"
                  v-text="$t('pages.CompaniesReviews.reviewerEditedAtOrder')"
                />
                <button
                  class="c-buttonSort__item"
                  :class="`is-${sortKey === 'rating' && sortOrder}`"
                  type="button"
                  :disabled="loading"
                  @click="sort('rating')"
                  v-text="$t('pages.CompaniesReviews.ratingOrder')"
                />
              </div>
              <DatePicker
                v-if="period"
                :initial-dates="period"
                opens="left"
                @input="changePeriod"
              />
              <div v-if="canExportAuthority('reviews') && providedFeaturesCsvExport">
                <KkButton
                  class="csvButton"
                  color="secondary"
                  full
                  height="38px"
                  :font-bold="false"
                  :text="$t('labels.csvDownload')"
                  @click="clickCreateCsv"
                />
              </div>
            </div>
          </div>
        </form>
        <div class="p-box__body">
          <Loading v-show="loading" />
          <div v-if="reviews" v-show="!loading">
            <div
              v-if="Object.keys(displayFilterCondition).some(key => displayFilterCondition[key] && displayFilterCondition[key].length !== 0)"
              class="filterConditionWrapper"
            >
              <FilterCondition
                :search-attributes="searchAttributes"
                :form-values="displayFilterCondition"
                :disable-filter-item-types="['competitors', 'competitor_brands', 'emails', 'keyword_analysis_targets', 'topic_types']"
                :display-first-single-line-types="['comments', 'replies', 'ratings']"
                :can-save="showMoreThanEditorAuthority"
                clickable-tag
                @open-setting-email-notification-modal="openSettingEmailNotificationModal"
                @open-setting-keyword-modal="openSettingKeywordModal"
              />
            </div>
            <div v-if="replyApprovalFlow" class="approvalSummary">
              <KkApprovalSummaryItem
                :is-active="selectedApprovalStatusType === 'all'"
                type="all"
                :count="filteredCount"
                :title="$t('pages.CompaniesReviews.totalCountLabel')"
                @click="clickApprovalSummary('all')"
              />
              <KkApprovalSummaryItem
                :is-active="selectedApprovalStatusType === 'requesting'"
                type="requesting"
                :count="requestingCount"
                @click="clickApprovalSummary('requesting')"
              />
              <KkApprovalSummaryItem
                :is-active="selectedApprovalStatusType === 'rejected'"
                type="rejected"
                :count="rejectedCount"
                @click="clickApprovalSummary('rejected')"
              />
            </div>
            <div v-if="reviews.data.length" class="p-reviews">
              <div v-if="!replyApprovalFlow" class="p-reviews__total_count">
                {{ $t('pages.CompaniesReviews.totalCount', { number: numberLocaleString(reviews.meta.total_count) }) }}
              </div>
              <!-- NOTE: 返信リンクの制御に関する暫定対応
                 Googleの口コミにおいて
                 API経由のレビューの場合 レビュアー名あり
                 Web Crawler経由のレビューの場合 レビュアー名なし
                 返信リンクの表示をGoogleAPI連携済み且つAPI経由の口コミの場合とする
              -->
              <ReviewItem
                v-for="review in reviews.data"
                :key="review.id"
                :review="review"
                :search-query="$route.query['query']"
                :reply-approval-flow="replyApprovalFlow"
                :reviews-request-params-for-apply="reviewsRequestParamsForApply"
                @data-refresh="handleSubmit"
              />
            </div>
            <div v-else v-text="$t('messages.noData')" />
            <div v-if="reviews.meta.total_pages" class="p-controller">
              <div class="p-controller__count">
                <p class="p-controller__count__name">
                  {{ $t('labels.perPageCount') }}
                </p>
                <div class="p-controller__count__select">
                  <select :value="displayNumber" @change="changeDisplayNumber($event)">
                    <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                  </select>
                </div>
                <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
              </div>
              <div class="p-controller__navi">
                <button
                  type="button"
                  :disabled="loading || currentPage === 1"
                  class="p-controller__navi__prev"
                  @click="goPrevPage"
                >
                  <Icon name="arrowLeft" />
                </button>
                <button
                  type="button"
                  :disabled="loading || currentPage >= reviews.meta.total_pages"
                  class="p-controller__navi__next"
                  @click="goNextPage"
                >
                  <Icon name="arrowRight" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 絞り込み条件モーダル -->
    <KkFilterModal
      :is-visible="modal"
      :filter-items="filterItems"
      :form-values="formValues"
      :total-count="filteredCount"
      @close="modal = false"
      @submit="confirmFilters"
      @reset="reset"
      @change-check-box="handleChangeFilterModalCheckBox"
    />
    <SettingEmailNotificationModal
      v-if="settingEmailNotificationModal"
      :users="userCompanies"
      :selected-users="selectedUserCompanies"
      :form="form"
      :is-visible="settingEmailNotificationModal"
      @change-form-value="changeFormValue"
      @add-user-companies="addUserCompanies"
      @remove-user-companies="removeUserCompanies"
      @add-all-user-companies="addAllUserCompanies"
      @submit="saveSearchPattern"
      @close="closeSettingEmailNotificationModal"
    />
    <KkSettingKeywordModal
      v-if="showSettingKeywordModal"
      :is-visible="showSettingKeywordModal"
      :selected-tag="selectedTag"
      :loading="loading"
      @close="showSettingKeywordModal = false"
      @specify="specifyKeyword"
      @ignore="ignoreKeyword"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import moment from 'moment'
import Loading from '../components/Loading.vue'
import Icon from '../components/Icon.vue'
import ReviewItem from '../components/CompaniesReviews/ReviewItem.vue'
import DatePicker from '../components/DatePicker'
import { KkApprovalSummaryItem } from '../components/base'
import GuideToSetUpSite from '../components/GuideToSetUpSite'
import { i18n } from '../plugins/i18n'
import { toNumberLocaleString } from '../modules/localeString'
import { KkFilterModal } from '../components/projects'
import { KkButton } from '../components/base'
import { addAttributes, comments, replies } from '../modules/filterModalConst'
import FilterCondition from '../components/FilterCondition'
import SettingEmailNotificationModal from '../components/Modals/SettingEmailNotificationModal'
import { KkSettingKeywordModal } from '../components/domains/review'
import cloneDeep from 'lodash.clonedeep'
import { actionEvent } from '../modules/googleAnalytics.ts'
import { searchQueryAnalyzer } from '../modules/searchQueryAnalyzer'
import { rettyPlanSitesForFilter } from '../modules/rettyPlanSitesForFilter'

export default {
  name: 'CompaniesReviews',
  components: {
    KkFilterModal,
    Loading,
    Icon,
    ReviewItem,
    DatePicker,
    GuideToSetUpSite,
    FilterCondition,
    SettingEmailNotificationModal,
    KkButton,
    KkSettingKeywordModal,
    KkApprovalSummaryItem
  },
  data: function() {
    return {
      searchQuery: this.$route.query['query'] || '',
      currentPage: 1,
      sortKey: this.$route.query['sort_key'] || null,
      sortOrder: this.$route.query['sort_order'] || null,
      datePickerOpened: false,
      modal: false,
      filterItems: [],
      settingEmailNotificationModal: false,
      displayFilterCondition: {},
      form: {
        name: '',
        notificationConfig: 'daily',
        userCompanyIds: [],
        day: 1,
        wday: 'sunday'
      },
      showSettingKeywordModal: false,
      selectedTag: '',
      selectedApprovalStatusType: 'all',
    }
  },
  computed: {
    ...mapState({
      reviews: state => state.companiesReviews.data,
      reviewsLoading: state => state.companiesReviews.loading,
      formValues: state => state.companiesReviews.formValues,
      filteredCount: state => state.companiesReviews.filteredCount,
      rejectedCount: state => state.companiesReviews.rejectedCount,
      requestingCount: state => state.companiesReviews.requestingCount,
      company: state => state.companiesCompany.data,
      companyLoading: state => state.companiesCompany.loading,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      displayNumber: state => state.filters.displayNumber,
      period: state => state.filters.period,
      userCompanies: state => state.companiesUserCompany.data,
      userCompaniesLoading: state => state.companiesUserCompany.loading,
      searchPatternId: state => state.companiesSearchPatterns.searchPatternId,
      searchPattern: state => state.companiesSearchPatterns.data,
      createKeywordLoading: state => state.companiesAnalysisSettingsKeywords.loading,
      ignoreKeywordLoading: state => state.companiesAnalysisSettingsIgnores.loading
    }),
    ...mapGetters({
      'canReplyAuthority': 'companiesAccessibleApi/canReplyAuthority',
      'canExportAuthority': 'companiesAccessibleApi/canExportAuthority',
      'providedFeaturesCsvExport': 'companiesCompany/providedFeaturesCsvExport',
      'replyApprovalFlow': 'companiesCompany/replyApprovalFlow',
      'showMoreThanEditorAuthority': 'companiesAccessibleApi/showMoreThanEditorAuthority',
      'inboundOption': 'companiesCompany/inboundOption',
      'isRettyPlan': 'companiesCompany/isRettyPlan'
    }),
    ratings: () => gon.ratings,
    comments: () => comments,
    replies: () => replies,
    commonQuery() {
      return {
        reviewer_edited_at_term: {
          start_date: this.period.startDate,
          end_date: this.period.endDate
        },
        store_uuids: this.formValues.stores,
        brand_ids: this.formValues.brands,
        site_types: this.formValues.sites,
        franchisee_ids: this.formValues.franchisees,
        user_company_ids: this.formValues.user_companies,
        area_ids: this.formValues.areas,
        prefectures: this.formValues.prefectures,
        ratings: this.formValues.ratings,
        comments: this.formValues.comments,
        replies: this.formValues.replies,
        locales: this.formValues.locales,
        original_tag_group_a_tag_ids: this.formValues.original_tag_group_a,
        original_tag_group_b_tag_ids: this.formValues.original_tag_group_b,
        original_tag_group_c_tag_ids: this.formValues.original_tag_group_c,
        original_tag_group_d_tag_ids: this.formValues.original_tag_group_d,
        original_tag_group_e_tag_ids: this.formValues.original_tag_group_e,
      }
    },
    loading() {
      return this.reviewsLoading || this.companyLoading || this.searchAttributesLoading || this.userCompaniesLoading || this.createKeywordLoading || this.ignoreKeywordLoading
    },
    startDataNumber: function() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber: function() {
      return this.startDataNumber + this.reviews.data.length - 1
    },
    currentCompanyUuid: function () {
      return this.$route.params.company_uuid
    },
    noConnectedSites: function() {
      return this.company?.attributes?.num_of_stores_connected_gmb === 0 && this.company?.attributes?.num_of_stores_connected_sites === 0
    },
    selectedUserCompanies() {
      return this.userCompanies.filter(user => this.form.userCompanyIds.includes(user.id))
    },
    readReviewsParams() {
      return {
        company_uuid: this.currentCompanyUuid,
        query: this.searchQuery,
        values: {
          biz_companies_review_form: {
            ...this.commonQuery,
            approval_status: this.selectedApprovalStatusType,
            page: this.currentPage,
            per: Number(this.displayNumber),
            sort_key: this.sortKey,
            sort_order: this.sortOrder,
          }
        }
      }
    },
    reviewsRequestParamsForApply() {
      return {
        biz_companies_review_form: {
          ...this.commonQuery,
          approval_status: this.selectedApprovalStatusType,
          page: this.currentPage,
          per: Number(this.displayNumber),
          sort_key: this.sortKey,
          sort_order: this.sortOrder,
        },
        query: this.searchQuery,
      }
    },
  },
  watch: {
    $route(to) {
      if (!Object.keys(to.query).length) {
        this.initSearch()
      }
      this.handleResetPageAndSubmit()
    }
  },
  async created() {
    $('a.l-menu__name.menu_item__reviews').addClass('is-current')  // TODO: フルSPA化までの暫定実装
    await this.readSearchAttributes()
    if (this.$route.query['search_pattern_id']) {
      await this.readSearchPattern(this.$route.query['search_pattern_id'])
    }
    if (this.$route.query['start_date'] && this.$route.query['end_date']) {
      this.setPeriod({
        startDate: this.$route.query['start_date'],
        endDate: this.$route.query['end_date'],
      })
    }

    if (this.$route.query['search_pattern_id'] && this.searchPattern != null) {
      this.setFormValues(this.fromSearchPattern())
      this.createFilter()
      this.searchQuery = this.searchPattern.condition.query || undefined
      const query = { 'query': this.searchQuery }
      this.$router.push({ query: query })
      this.saveDisplayFilterCondition()
    } else {
      const newFormValues = this.formValuesFromQueryParams()
      const {
        analysis_type,
        analysis_id,
        cross_analysis_type,
        cross_analysis_id,
      } = this.$route.query
      if (analysis_type && analysis_id) {
        const id = ['stores', 'locales'].includes(analysis_type) ? analysis_id : Number(analysis_id)
        newFormValues[analysis_type].push(id)
      }
      if (cross_analysis_type && cross_analysis_id) {
        const id = ['stores', 'locales'].includes(cross_analysis_type) ? cross_analysis_id : Number(cross_analysis_id)
        newFormValues[cross_analysis_type].push(id)
      }
      this.setFormValues(newFormValues)
      this.createFilter()
      this.handleSubmit()
    }
    this.clearUnreadReviewsCount({ companyUuid: this.currentCompanyUuid })
    this.readUserCompanies()
  },
  methods: {
    ...mapMutations({
      setFormValues: 'companiesReviews/setFormValues',
    }),
    ...mapActions({
      readReviews: 'companiesReviews/readReviews',
      readReviewsForFilter: 'companiesReviews/readReviewsForFilter',
      submitReply: 'companiesReviews/submitReply',
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      clearUnreadReviewsCount: 'companiesUserActivity/clearUnreadReviewsCount',
      createCsv: 'companiesReviews/createCsv',
      setDisplayNumber: 'filters/setDisplayNumber',
      setPeriod: 'filters/setPeriod',
      readUserCompanies: 'companiesUserCompany/readUserCompanies',
      createSearchPattern: 'companiesSearchPatterns/createSearchPattern',
      createsNotificationConfigs: 'companiesSearchPatternsNotificationConfigs/createsNotificationConfigs',
      setSearchPatternId: 'companiesSearchPatterns/setSearchPatternId',
      resetSearchPatternId: 'companiesSearchPatterns/resetSearchPatternId',
      readSearchPattern:  'companiesSearchPatterns/readSearchPattern',
      deleteSearchPattern: 'companiesSearchPatterns/deleteSearchPattern',
      createKeyword: 'companiesAnalysisSettingsKeywords/createKeyword',
      createIgnore: 'companiesAnalysisSettingsIgnores/createIgnore',
    }),
    clickApprovalSummary(type) {
      if (this.selectedApprovalStatusType === type) return
      this.selectedApprovalStatusType = type
      this.handleSubmit()
    },
    initData() {
      this.currentPage = 1
      this.sortKey = null
      this.sortOrder = null
    },
    openModal: function() {
      if (this.loading) return

      this.modal = true
    },
    closeModal: function() {
      this.modal = false
    },
    formInitialValues: function() {
      return {
        stores: [],
        sites: [],
        brands: [],
        franchisees: [],
        areas: [],
        user_companies: [],
        prefectures: [],
        ratings: [],
        comments: [],
        replies: [],
        locales: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: []
      }
    },
    formValuesFromQueryParams() {
      /**
       * $route.query['xxxxx'] の値は string or array or undefined のいずれか
       * ここで array に変換する
       */
      const {
        stores,
        sites,
        brands,
        franchisees,
        areas,
        user_companies: userCompanies,
        prefectures,
        ratings,
        comments,
        replies,
        locales,
        original_tag_group_a: originalTagGroupA,
        original_tag_group_b: originalTagGroupB,
        original_tag_group_c: originalTagGroupC,
        original_tag_group_d: originalTagGroupD,
        original_tag_group_e: originalTagGroupE,
      } = this.$route.query

      const storesConvertToArray = []
      if (typeof stores === 'string') {
        storesConvertToArray.push(...stores.split(','))
      } else if (Array.isArray(stores)) {
        storesConvertToArray.push(...stores)
      }

      const sitesConvertToArray = []
      if (typeof sites === 'string') {
        sitesConvertToArray.push(...sites.split(',').map(Number))
      } else if (Array.isArray(sites)) {
        sitesConvertToArray.push(...sites.map(Number))
      }

      const brandsConvertToArray = []
      if (typeof brands === 'string') {
        brandsConvertToArray.push(...brands.split(',').map(Number))
      } else if (Array.isArray(brands)) {
        brandsConvertToArray.push(...brands.map(Number))
      }

      const franchiseesConvertToArray = []
      if (typeof franchisees === 'string') {
        franchiseesConvertToArray.push(...franchisees.split(',').map(Number))
      } else if (Array.isArray(franchisees)) {
        franchiseesConvertToArray.push(...franchisees.map(Number))
      }

      const areasConvertToArray = []
      if (typeof areas === 'string') {
        areasConvertToArray.push(...areas.split(',').map(Number))
      } else if (Array.isArray(areas)) {
        areasConvertToArray.push(...areas.map(Number))
      }

      const userCompaniesConvertToArray = []
      if (typeof userCompanies === 'string') {
        userCompaniesConvertToArray.push(...userCompanies.split(',').map(Number))
      } else if (Array.isArray(userCompanies)) {
        userCompaniesConvertToArray.push(...userCompanies.map(Number))
      }

      const prefecturesConvertToArray = []
      if (typeof prefectures === 'string') {
        prefecturesConvertToArray.push(...prefectures.split(',').map(Number))
      } else if (Array.isArray(prefectures)) {
        prefecturesConvertToArray.push(...prefectures.map(Number))
      }

      const ratingsConvertToArray = []
      if (typeof ratings === 'string') {
        ratingsConvertToArray.push(...ratings.split(',').map(Number))
      } else if (Array.isArray(ratings)) {
        ratingsConvertToArray.push(...ratings.map(Number))
      }

      const commentsConvertToArray = []
      if (typeof comments === 'string') {
        commentsConvertToArray.push(...comments.split(',').map(comment => comment === 'true'))
      } else if (Array.isArray(comments)) {
        commentsConvertToArray.push(...comments.map(comment => comment === 'true'))
      }

      const repliesConvertToArray = []
      if (typeof replies === 'string') {
        repliesConvertToArray.push(...replies.split(','))
      } else if (Array.isArray(replies)) {
        repliesConvertToArray.push(...replies)
      }

      const localesConvertToArray = []
      if (typeof locales === 'string') {
        localesConvertToArray.push(...locales.split(','))
      } else if (Array.isArray(locales)) {
        localesConvertToArray.push(...locales)
      }

      const originalTagGroupAConvertToArray = []
      if (typeof originalTagGroupA === 'string') {
        originalTagGroupAConvertToArray.push(...originalTagGroupA.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupA)) {
        originalTagGroupAConvertToArray.push(...originalTagGroupA.map(Number))
      }

      const originalTagGroupBConvertToArray = []
      if (typeof originalTagGroupB === 'string') {
        originalTagGroupBConvertToArray.push(...originalTagGroupB.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupB)) {
        originalTagGroupBConvertToArray.push(...originalTagGroupB.map(Number))
      }

      const originalTagGroupCConvertToArray = []
      if (typeof originalTagGroupC === 'string') {
        originalTagGroupCConvertToArray.push(...originalTagGroupC.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupC)) {
        originalTagGroupCConvertToArray.push(...originalTagGroupC.map(Number))
      }

      const originalTagGroupDConvertToArray = []
      if (typeof originalTagGroupD === 'string') {
        originalTagGroupDConvertToArray.push(...originalTagGroupD.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupD)) {
        originalTagGroupDConvertToArray.push(...originalTagGroupD.map(Number))
      }

      const originalTagGroupEConvertToArray = []
      if (typeof originalTagGroupE === 'string') {
        originalTagGroupEConvertToArray.push(...originalTagGroupE.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupE)) {
        originalTagGroupEConvertToArray.push(...originalTagGroupE.map(Number))
      }

      return {
        stores: storesConvertToArray,
        sites: sitesConvertToArray,
        brands: brandsConvertToArray,
        franchisees: franchiseesConvertToArray,
        areas: areasConvertToArray,
        user_companies: userCompaniesConvertToArray,
        prefectures: prefecturesConvertToArray,
        ratings: ratingsConvertToArray,
        comments: commentsConvertToArray,
        replies: repliesConvertToArray,
        locales: this.inboundOption ? localesConvertToArray : [],
        original_tag_group_a: originalTagGroupAConvertToArray,
        original_tag_group_b: originalTagGroupBConvertToArray,
        original_tag_group_c: originalTagGroupCConvertToArray,
        original_tag_group_d: originalTagGroupDConvertToArray,
        original_tag_group_e: originalTagGroupEConvertToArray,
      }
    },
    fromSearchPattern: function() {
      const condition = this.searchPattern.condition
      return {
        stores: condition.storeUuids,
        sites: condition.siteTypes,
        brands: condition.brandIds,
        franchisees: condition.franchiseeIds,
        areas: condition.areaIds,
        user_companies: condition.userCompanyIds,
        prefectures: condition.prefectures,
        ratings: condition.ratings,
        comments: condition.comments,
        replies: condition.replies,
        locales: condition.locales,
        original_tag_group_a: condition.originalTagGroupATagIds,
        original_tag_group_b: condition.originalTagGroupBTagIds,
        original_tag_group_c: condition.originalTagGroupCTagIds,
        original_tag_group_d: condition.originalTagGroupDTagIds,
        original_tag_group_e: condition.originalTagGroupETagIds,
      }
    },
    basicDateMonthFormat: function( date ) {
      return date ? moment( date ).format(this.$t('format.date.excludeDay')) : ''
    },
    sort: function(key) {
      if (this.loading) return

      if (this.sortKey === key) {
        this.sortOrder = (this.sortOrder === 'desc' ) ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }

      const newQuery = { ...this.$route.query }
      newQuery.sort_order = this.sortOrder
      newQuery.sort_key = this.sortKey
      this.$router.push({ query: newQuery })
    },
    reset: function() {
      this.setFormValues(this.formInitialValues())
      const newQuery = { ...this.$route.query }
      delete newQuery.analysis_type
      delete newQuery.analysis_id
      for (const key in this.formValues) {
        delete newQuery[key]
      }

      this.closeModal()
      this.$router.push({ query: newQuery })
    },
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    initSearch() {
      this.searchQuery = ''
      this.initData()
    },
    /**
     * 表示件数の変更
     * @param  { Object } e
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
    },
    /**
     * 期間の変更
     * @param  { Object } value
     * @return { Void }
     */
    changePeriod(value) {
      this.setPeriod(value)

      const newQuery = { ...this.$route.query }
      newQuery.start_date = value.startDate
      newQuery.end_date = value.endDate
      this.$router.push({ query: newQuery })
    },
    search(event) {
      if (this.loading) return

      if (event.keyCode !== 13) return
      if (this.searchQuery === (this.$route.query['query'] || '')) return

      const searchQueries = searchQueryAnalyzer(this.searchQuery)
      const queryError = searchQueries.some(query => query.length === 1)
      if (queryError) {
        alert(this.$t('pages.CompaniesReviews.alert.keyword'))
        return
      }

      const newQuery = { ...this.$route.query }
      newQuery.query = this.searchQuery || undefined
      this.$router.push({ query: newQuery })
    },
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    async clickCreateCsv() {
      if (this.reviews.meta.total_count === 0) {
        this.$updateFlash({ type: 'alert', message: i18n.t('pages.CompaniesExportResults.emptyData.review') })
        return
      }

      const payload = {
        company_uuid: this.currentCompanyUuid,
        values: {
          biz_companies_review_form: {
            ...this.commonQuery,
            per: this.reviews.meta.total_count,
            sort_key: this.sortKey,
            sort_order: this.sortOrder,
            query: this.searchQuery,
          }
        }
      }
      await this.createCsv(payload)
      this.$router.push({ name: 'CompaniesExportResults' })
      actionEvent('CsvDownload')
    },
    handleSubmit() {
      this.readReviews(this.readReviewsParams)
      this.saveDisplayFilterCondition()
      this.datePickerOpened = false
      this.closeModal()
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)
      this.readReviewsForFilter(this.readReviewsParams)
    },
    createFilter() {
      if (this.isRettyPlan) {
        this.searchAttributes.sites.tags = this.searchAttributes.sites.tags.filter(tag => rettyPlanSitesForFilter.includes(tag.name))
      }
      const attributes = [
        this.searchAttributes.comments,
        this.searchAttributes.replies,
        this.searchAttributes.ratings,
        this.inboundOption ? this.searchAttributes.locales : null,
        this.searchAttributes.sites,
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
      ].filter(v => v != null)

      if (this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if (this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if (this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if (this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if (this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)

      this.filterItems = attributes.map((attr) => {
        if (attr && attr.kind) {
          return addAttributes(attr, attr.kind)
        }
      })
    },
    openSettingEmailNotificationModal() {
      this.settingEmailNotificationModal = true
    },
    changeFormValue({value, target}) {
      this.form[target] = value
    },
    addUserCompanies(value) {
      this.form.userCompanyIds.push(value)
    },
    removeUserCompanies(removeId) {
      this.form.userCompanyIds = this.form.userCompanyIds.filter(id => removeId !== id)
    },
    addAllUserCompanies() {
      this.form.userCompanyIds = this.userCompanies.map(user => user.id)
    },
    saveDisplayFilterCondition() {
      const params = cloneDeep(this.formValues)
      delete params['posted_at_term']
      params['query'] = this.searchQuery
      this.displayFilterCondition = params
    },
    confirmFilters() {
      const newQuery = { ...this.$route.query }
      for (const key in this.formValues) {
        newQuery[key] = this.formValues[key]
      }
      this.closeModal()
      this.$router.push({ query: newQuery })
      actionEvent('ReviewFilter', JSON.stringify(newQuery))
    },
    resetSettingEmailNotificationModal(){
      this.form = {
        name: '',
        notificationConfig: 'daily',
        userCompanyIds: []
      }
      this.resetSearchPatternId()
      this.settingEmailNotificationModal = false
    },
    async saveSearchPattern() {
      const searchPatternPayload = {
        search_pattern: {
          name: this.form.name,
          kind: 'review',
          condition: {
            store_uuids: this.displayFilterCondition.stores,
            brand_ids: this.displayFilterCondition.brands,
            site_types: this.displayFilterCondition.sites,
            franchisee_ids: this.displayFilterCondition.franchisees,
            user_company_ids: this.displayFilterCondition.user_companies,
            area_ids: this.displayFilterCondition.areas,
            prefectures: this.displayFilterCondition.prefectures,
            ratings: this.displayFilterCondition.ratings,
            comments: this.displayFilterCondition.comments,
            replies: this.displayFilterCondition.replies,
            locales: this.displayFilterCondition.locales,
            original_tag_group_a_tag_ids: this.displayFilterCondition.original_tag_group_a,
            original_tag_group_b_tag_ids: this.displayFilterCondition.original_tag_group_b,
            original_tag_group_c_tag_ids: this.displayFilterCondition.original_tag_group_c,
            original_tag_group_d_tag_ids: this.displayFilterCondition.original_tag_group_d,
            original_tag_group_e_tag_ids: this.displayFilterCondition.original_tag_group_e,
            query: this.searchQuery,
          }
        }
      }

      const notificationConfigPayload = {
        notification_config: {
          kind: this.form.notificationConfig,
          user_company_ids: this.form.userCompanyIds
        }
      }
      if(this.form.notificationConfig === 'weekly') notificationConfigPayload['notification_config']['wday'] = this.form.wday
      if(this.form.notificationConfig === 'monthly') notificationConfigPayload['notification_config']['day'] = this.form.day
      if(!this.searchPatternId) await this.createSearchPattern(searchPatternPayload)
      const isSuccess = await this.createsNotificationConfigs({ searchPatternId: this.searchPatternId,  payload: notificationConfigPayload })
      if(isSuccess) this.resetSettingEmailNotificationModal()
    },
    async closeSettingEmailNotificationModal() {
      if(this.searchPatternId) await this.deleteSearchPattern(this.searchPatternId)
      this.resetSearchPatternId()
      this.settingEmailNotificationModal = false
    },
    openSettingKeywordModal(value) {
      this.showSettingKeywordModal = true
      this.selectedTag = value
    },
    async specifyKeyword(value) {
      const payload = {
        rule: value,
      }
      const isSuccess = await this.createKeyword({ company_uuid: this.currentCompanyUuid, payload: payload })
      if (isSuccess) {
        this.showSettingKeywordModal = false
      }
    },
    async ignoreKeyword(value) {
      const payload = {
        rule: value,
      }
      const isSuccess = await this.createIgnore({ company_uuid: this.currentCompanyUuid, payload: payload })
      if (isSuccess) this.showSettingKeywordModal = false
    }
  }
}
</script>

<style lang="scss" scoped>
.searchForm {
  height: 38px;
  padding: 8px 6px 8px 48px;
}
.sortForm {
  margin-left: 16px !important;
}

// TODO: スクロールしないほうが良いとのことだったので一旦コメントアウト
// .scrollFiltersItem {
//   height: 460px;
//   overflow-y: scroll;
// }
.c-button__item {
  margin: 0 8px;
}
.csvButton {
  margin-left: 10px;
  @media screen and (max-width: 768px) {
    margin-left: 0;
    margin-top: 16px;
  }
}
.filterConditionWrapper {
  margin-bottom: 16px;
}
.approvalSummary {
  margin-bottom: 16px;
  display: flex;
  gap: 0 16px;
}
</style>
